import { useTranslation } from "react-i18next";
import { SEOHead } from "../components/SEOHead";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Mail, MessageSquare, Send } from "lucide-react";
import { SiteFooter } from "../components/SiteFooter";
import { useState } from "react";
import { toast } from "sonner";

export default function ContactPage() {
  const { t } = useTranslation();
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sent, setSent] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    setSent(true);
    toast.success(t("contact.successTitle"));
  };

  return (
    <div className="min-h-screen bg-background" data-testid="contact-page">
      <SEOHead title={t("contact.title")} />
      <main className="max-w-2xl mx-auto px-4 md:px-6 py-12 md:py-20">
        <div className="text-center mb-10">
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 font-heading mb-3">{t("contact.title")}</h1>
          <p className="text-base text-slate-600">{t("contact.subtitle")}</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-10">
          <Card className="border-slate-100 shadow-sm">
            <CardContent className="p-5 flex items-start gap-3">
              <Mail className="h-5 w-5 text-teal-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-semibold text-slate-900 mb-0.5">{t("contact.email")}</p>
                <p className="text-sm text-slate-600">support@immigrationguide.us</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-slate-100 shadow-sm">
            <CardContent className="p-5 flex items-start gap-3">
              <MessageSquare className="h-5 w-5 text-teal-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-semibold text-slate-900 mb-0.5">{t("contact.responseTime")}</p>
                <p className="text-sm text-slate-600">{t("contact.responseValue")}</p>
              </div>
            </CardContent>
          </Card>
        </div>

        <Card className="border-slate-200 shadow-sm" data-testid="contact-form-card">
          <CardContent className="p-6 md:p-8">
            {sent ? (
              <div className="text-center py-8" data-testid="contact-success">
                <div className="h-14 w-14 rounded-full bg-teal-50 flex items-center justify-center mx-auto mb-4">
                  <Send className="h-6 w-6 text-teal-600" />
                </div>
                <h2 className="text-xl font-semibold text-slate-900 mb-2">{t("contact.successTitle")}</h2>
                <p className="text-sm text-slate-600 mb-6">{t("contact.successDesc")}</p>
                <Button variant="outline" onClick={() => { setSent(false); setForm({ name: "", email: "", message: "" }); }} data-testid="contact-send-another">
                  {t("contact.sendAnother")}
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
                <div className="space-y-2">
                  <Label htmlFor="contact-name" className="text-sm font-medium text-slate-700">{t("contact.name")}</Label>
                  <Input id="contact-name" required placeholder={t("contact.namePh")} className="h-12 border-slate-200" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="contact-name-input" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="contact-email" className="text-sm font-medium text-slate-700">{t("contact.emailLabel")}</Label>
                  <Input id="contact-email" type="email" required placeholder={t("contact.emailPh")} className="h-12 border-slate-200" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="contact-email-input" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="contact-message" className="text-sm font-medium text-slate-700">{t("contact.message")}</Label>
                  <Textarea id="contact-message" required placeholder={t("contact.messagePh")} rows={5} className="border-slate-200" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="contact-message-input" />
                </div>
                <Button type="submit" className="w-full h-12 bg-teal-700 hover:bg-teal-800 text-white font-medium text-base" data-testid="contact-submit-btn">
                  <Send className="mr-2 h-4 w-4" /> {t("contact.send")}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      </main>
      <SiteFooter />
    </div>
  );
}
